import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ExpandMoreRounded } from '@mui/icons-material';
import {
  Checkbox,
  Container,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import color from 'src/assets/_util.scss';
import Button from 'src/components/core/Button/Button';
import LoadingSpinner from 'src/components/core/LoadingSpinner/LoadingSpinner';
import { VSpacer } from 'src/components/core/Spacer';
import { HStack } from 'src/components/core/Stack';
import catalogSelectors from 'src/redux/catalog/catalog-selectors';
import { StoreState } from 'src/redux/store';
import {
  Entry,
  Filter,
  FilterJoin,
  FilterName,
  FilterOperation,
} from 'src/types/insights';
import { TransitionSineOut } from 'src/util/styles';
import HighlightCard from '../Catalog/HighlightCard';
import { useHighlightsPageContext } from '../Catalog/HighlightsPageProvider';
import CodeChip from '../Common/CodeChip';
import {
  useFilterLabels,
  useFilterOperations,
  useFilterOptions,
  valuelessOperations,
} from '../utils/filters';

const FilterableHighlightGrid = () => {
  const {
    isLoading,
    visibleHighlights,
    selectedHighlightIds,
    setSelectedHighlightIds,
    openFilters,
    selectedFilterJoin,
    filterJoin,
    setFilterJoin,
    selectedFilters,
    filters,
    initialFilter,
    setFilters,
    applyFilters: _applyFilters,
    selectEntry,
    setOpenFilters,
    t,
  } = useHighlightsPageContext();

  const onClick = useCallback(
    (entry: Entry) => selectEntry(entry.id),
    [selectEntry]
  );

  const onChangeCheck = useCallback(
    (checked: boolean, highlight: Entry) => {
      const nowSelectedHighlights = new Set(selectedHighlightIds);
      nowSelectedHighlights[checked ? 'add' : 'delete'](highlight.id);
      setSelectedHighlightIds(nowSelectedHighlights);
    },
    [selectedHighlightIds, setSelectedHighlightIds]
  );

  const addFilter = () => {
    const newFilters = [...filters];
    newFilters.push(initialFilter);
    setFilters(newFilters);
  };

  const updateFilter = (ind: number, filter: Filter) => {
    const newFilters = [...filters];
    newFilters.splice(ind, 1, filter);
    setFilters(newFilters);
  };

  const deleteFilter = (ind: number) => {
    let newFilters: Filter[] = [{ ...initialFilter }];
    if (filters.length !== 1) {
      newFilters = [...filters].filter((_, x) => x !== ind);
    }
    setFilters(newFilters);
  };
  const curFilterQuery = useSelector((state: StoreState) =>
    catalogSelectors.getFiltersQuery(state, filters, filterJoin)
  );
  const prevFilterQuery = useSelector((state: StoreState) =>
    catalogSelectors.getFiltersQuery(state, selectedFilters, selectedFilterJoin)
  );
  const filtersUpdated = curFilterQuery !== prevFilterQuery;

  const applyFilters = () => {
    if (filtersUpdated) {
      // Only update state with filters containing a value
      let filtersWithValues = filters.filter(
        (f) => f.values.length || valuelessOperations.includes(f.operation)
      );
      if (filtersWithValues.length === 0) {
        //we are resetting the filters
        filtersWithValues = [{ ...initialFilter }];
      }
      _applyFilters(filtersWithValues, filterJoin);
    }
    setOpenFilters(false);
  };

  const canDeleteFilters = filters.length > 1 || filters[0]?.values.length;

  return (
    <Grid
      container
      sx={{
        overflow: 'hidden',
      }}
      data-testid="insights_filters_container"
    >
      <Grid
        item
        xs={6}
        style={{
          borderRight: `1px solid ${color.gray300}`,
          display: openFilters ? 'block' : 'none',
        }}
        sx={{ height: '100%', overflow: 'auto' }}
      >
        <Container
          sx={{
            width: '100%',
            marginBottom: '100px',
          }}
        >
          <Typography variant="h5" sx={{ padding: '24px 0 12px ' }}>
            {t('insights.filters_header')}
          </Typography>
          <Typography>{t('insights.filters_description')}</Typography>
          <Trans
            i18nKey="insights.filters_helper"
            /*eslint-disable */
            components={{
              1: (
                <a
                  href="https://help.cortico.ai/hc/en-us/articles/11984672369815-Filtering-your-highlights"
                  rel="noopener noreferrer"
                  target="_blank"
                />
              ),
            }}
            /*eslint-disable */
          />
          <VSpacer />
          <HStack
            style={{
              alignItems: 'center',
              paddingTop: '24px',
              justifyContent: 'space-between',
            }}
          >
            <Typography fontWeight={600} data-testid="insights_filter_count">
              {t('insights.filters_showing', {
                count: visibleHighlights?.length ?? 0,
              })}
            </Typography>
            <Button
              onClick={applyFilters}
              style={{
                color: color[filtersUpdated ? 'white' : 'foraPurple'],
                backgroundColor:
                  color[filtersUpdated ? 'foraPurple' : 'gray300'],
              }}
              disabled={!filtersUpdated}
              data-testid="insights_apply_filters_button"
            >
              <Typography>{t('insights.filters_apply')}</Typography>
            </Button>
          </HStack>

          {filters?.map((filter, ind) => (
            <FilterInputRow
              index={ind}
              key={ind}
              filter={filter}
              updateFilter={updateFilter}
              deleteFilter={canDeleteFilters ? deleteFilter : undefined}
              filterJoin={filterJoin}
              setFilterJoin={setFilterJoin}
            />
          ))}
          <Button
            icon={['far', 'plus']}
            onClick={addFilter}
            style={{
              color: color.foraPurple,
              boxShadow: 'none',
              border: 'none',
            }}
            disabled={filters.length >= 5}
            data-testid="insights_add_filter"
          >
            <Typography>{t('insights.filters_add')}</Typography>
          </Button>
        </Container>
      </Grid>
      <Grid
        item
        xs={openFilters ? 6 : 12}
        sx={{ height: '100%', position: 'relative' }}
      >
        <Grid
          container
          item
          spacing={3}
          xs={8}
          style={{
            maxWidth: '1280px',
            width: '100%',
            height: '100%',
            flexBasis: 'unset',
            margin: '0 auto',
            paddingTop: '.5rem',
            overflow: isLoading ? 'hidden' : 'auto',
            boxSizing: 'border-box',
            justifyContent: 'center',
            padding: '12px 12px 76px', // 76px leaves room for floating button
          }}
        >
          {visibleHighlights?.map((entry) => (
            <Grid
              item
              alignItems="stretch"
              lg={openFilters ? 12 : 6}
              key={entry.id}
              id={`highlight-${entry.annotation_id}`}
              sx={{
                padding: '12px !important',
                maxWidth: { sm: '650px' },
                width: '100%',
              }}
            >
              <HighlightCard
                entry={entry}
                key={entry.id}
                onClick={() => onClick(entry)}
                onChangeCheck={onChangeCheck}
                checked={selectedHighlightIds.has(entry.id)}
              />
            </Grid>
          ))}
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
              backgroundColor: color.white,
              zIndex: 100,
              pointerEvents: 'none',
              opacity: isLoading ? 1 : 0,
              ...TransitionSineOut,
            }}
          >
            <LoadingSpinner active size="lg" />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};

const FilterInputRow = ({
  index,
  filter,
  updateFilter,
  deleteFilter,
  filterJoin,
  setFilterJoin,
}: {
  index: number;
  filter: Filter;
  updateFilter: (ind: number, filter: Filter) => void;
  deleteFilter?: (ind: number) => void;
  filterJoin: FilterJoin;
  setFilterJoin: (join: FilterJoin) => void;
}) => {
  const { t } = useTranslation();
  const {
    filterJoinLabel,
    filterCategoryLabel,
    filterOperationsLabel,
    filterValueLabel,
  } = useFilterLabels();
  const filterOptionMap = useFilterOptions();
  const filterOptions = filterOptionMap[filter.filter_name];
  const filterOperationMap = useFilterOperations();
  const filterOperations = filterOperationMap[filter.filter_name];
  const [_filter, setFilter] = useState(filter);
  const thematic_codes = useSelector(catalogSelectors.getThematicCodes);
  const structural_codes = useSelector(catalogSelectors.getStructuralCodes);
  const internal_codes = useSelector(catalogSelectors.getInternalCodes);
  const demographic_codes = useSelector(catalogSelectors.getDemographics);

  // Only show filters that have options
  const availableFilters = Object.keys(filterOptionMap).filter(
    (key) => filterOptionMap[key as unknown as FilterName].length
  );

  const disableOptionsSelection = (operation: FilterOperation) =>
    valuelessOperations.includes(operation);

  const getOperationForNewCategory = (
    category: FilterName,
    operation: FilterOperation
  ): FilterOperation => {
    // If the new category has the existing operation, keep it, otherwise,
    // default to the first operation
    if (filterOperationMap[category].includes(operation)) {
      return operation;
    }
    return filterOperationMap[category][0];
  };

  const handleCategoryChange = (event: SelectChangeEvent<FilterName>) => {
    const newFilter: Filter = {
      operation: getOperationForNewCategory(
        event.target.value as unknown as FilterName,
        _filter.operation
      ),
      filter_name: event.target.value as unknown as FilterName,
      values: [], // value should be reset
    };
    setFilter(newFilter);
    updateFilter(index, newFilter);
  };

  const handleOperationChange = (event: SelectChangeEvent<FilterOperation>) => {
    let value = _filter.values;
    const operation = event.target.value as unknown as FilterOperation;
    if (disableOptionsSelection(operation)) {
      // If the change disables selection, clear the value field
      value = [];
    }
    const newFilter: Filter = {
      ..._filter,
      operation,
      values: value,
    };
    setFilter(newFilter);
    updateFilter(index, newFilter);
  };

  const handleValueChange = (
    event: SelectChangeEvent<(number | string | boolean)[]>
  ) => {
    const value = event.target.value as unknown as number[];
    const newFilter: Filter = {
      ..._filter,
      values: value,
    };
    setFilter(newFilter);
    updateFilter(index, newFilter);
  };

  useEffect(() => {
    if (filter !== _filter) {
      // Ensure deletion is propagated
      setFilter(filter);
    }
  }, [filter]);

  const getOptionSelectItems = (filterName: FilterName): ReactNode => {
    let items: ReactNode[] = [];
    if (
      [FilterName.AI_SUGGESTED_CODES, FilterName.APPLIED_CODES].includes(
        filterName
      )
    ) {
      if (thematic_codes.length) {
        items.push(
          <ListSubheader
            key={`thematic_codes_${index}`}
            style={{ fontSize: '16px', color: 'black' }}
          >
            {t(`insights.thematic_codes`)}
          </ListSubheader>
        );
        thematic_codes?.forEach((option) => {
          items.push(
            <MenuItem
              value={option.id}
              key={option.id}
              data-testid={`insights_filter_options_${index}_option`}
            >
              <Checkbox
                checked={option.parentage.some(
                  (id) => _filter.values.indexOf(id) > -1
                )}
                style={
                  option.parentage.length > 1 ? { marginLeft: '1rem' } : {}
                }
              />
              <ListItemText primary={<CodeChip code={option} noCount />} />
            </MenuItem>
          );
        });
      }
      if (structural_codes.length) {
        items.push(
          <ListSubheader
            key={`structural_codes_${index}`}
            style={{ fontSize: '16px', color: 'black' }}
          >
            {t(`insights.structural_codes`)}
          </ListSubheader>
        );
        structural_codes?.forEach((option) => {
          items.push(
            <MenuItem value={option.id} key={option.id}>
              <Checkbox
                checked={option.parentage.some(
                  (id) => _filter.values.indexOf(id) > -1
                )}
                style={
                  option.parentage.length > 1 ? { marginLeft: '1rem' } : {}
                }
              />
              <ListItemText primary={<CodeChip code={option} noCount />} />
            </MenuItem>
          );
        });
      }
      if (internal_codes.length) {
        items.push(
          <ListSubheader
            key={`internal_codes_${index}`}
            style={{ fontSize: '16px', color: 'black' }}
          >
            {t(`insights.internal_codes`)}
          </ListSubheader>
        );
        internal_codes?.forEach((option) => {
          items.push(
            <MenuItem value={option.id} key={option.id}>
              <Checkbox
                checked={option.parentage.some(
                  (id) => _filter.values.indexOf(id) > -1
                )}
                style={
                  option.parentage.length > 1 ? { marginLeft: '1rem' } : {}
                }
              />
              <ListItemText primary={<CodeChip code={option} noCount />} />
            </MenuItem>
          );
        });
      }
    } else if (FilterName.SPEAKER_DEMOGRAPHICS === filterName) {
      items = demographic_codes?.map((option) => (
        <MenuItem value={option.id} key={option.id}>
          <Checkbox
            checked={option.parentage.some(
              (id) => _filter.values.indexOf(id) > -1
            )}
            style={option.parentage.length > 1 ? { marginLeft: '1rem' } : {}}
          />
          <ListItemText primary={<CodeChip code={option} noCount />} />
        </MenuItem>
      ));
    } else {
      items = filterOptions?.map((option) => (
        <MenuItem value={option.id} key={option.id}>
          <Checkbox checked={_filter.values.indexOf(option.id) > -1} />
          <ListItemText primary={option.name} />
        </MenuItem>
      ));
    }
    return items;
  };

  return (
    <HStack
      sx={{ width: '100%', margin: '1rem 0' }}
      data-testid="insights_filter_row"
    >
      {index !== 0 && (
        <FormControl
          size="small"
          sx={{ maxWidth: '85px', flex: 2, margin: '0 .25rem' }}
        >
          <Select
            sx={{
              padding: '0px !important',
              borderRadius: '8px',
            }}
            value={filterJoin}
            disabled={index !== 1}
            onChange={(event) => {
              setFilterJoin(event.target.value as unknown as FilterJoin);
            }}
            IconComponent={ExpandMoreRounded}
            MenuProps={{
              sx: {
                '& .MuiPaper-root': {
                  marginTop: '.5rem',
                  boxShadow: '0px 2px 6px 0px #00000040 !important',
                },
              },
            }}
            data-testid={`insights_filter_join_${index}`}
          >
            {Object.keys(filterJoinLabel)?.map((key) => {
              return (
                <MenuItem value={key} key={key}>
                  {filterJoinLabel[key as unknown as FilterJoin]}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
      <FormControl size="small" sx={{ flex: 3, margin: '0 .25rem' }}>
        <Select
          sx={{
            padding: '0px !important',
            borderRadius: '8px',
          }}
          value={_filter.filter_name}
          onChange={handleCategoryChange}
          IconComponent={ExpandMoreRounded}
          MenuProps={{
            sx: {
              '& .MuiPaper-root': {
                marginTop: '.5rem',
                boxShadow: '0px 2px 6px 0px #00000040 !important',
              },
            },
          }}
          data-testid={`insights_filter_category_${index}`}
        >
          {availableFilters?.map((key) => {
            return (
              <MenuItem value={key} key={key}>
                {filterCategoryLabel[key as unknown as FilterName]}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ flex: 2, margin: '0 .25rem' }}>
        <Select
          sx={{
            padding: '0px !important',
            borderRadius: '8px',
          }}
          value={_filter.operation}
          onChange={handleOperationChange}
          IconComponent={ExpandMoreRounded}
          MenuProps={{
            sx: {
              '& .MuiPaper-root': {
                marginTop: '.5rem',
                boxShadow: '0px 2px 6px 0px #00000040 !important',
              },
            },
          }}
          data-testid={`insights_filter_operation_${index}`}
        >
          {filterOperations?.map((key) => {
            return (
              <MenuItem value={key} key={key}>
                {filterOperationsLabel[key as unknown as FilterOperation]}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {!disableOptionsSelection(_filter.operation) && (
        <FormControl size="small" sx={{ flex: 4, margin: '0 .25rem' }}>
          <InputLabel
            id="filter-options"
            shrink={false}
            sx={{
              visibility: _filter.values.length ? 'hidden' : 'visible',
            }}
          >
            {filterValueLabel[_filter.filter_name]}
          </InputLabel>
          <Select
            id="filter-options"
            sx={{
              padding: '0px !important',
              borderRadius: '8px',
            }}
            value={_filter.values}
            onChange={handleValueChange}
            multiple
            disabled={disableOptionsSelection(_filter.operation)}
            IconComponent={ExpandMoreRounded}
            MenuProps={{
              sx: {
                '& .MuiPaper-root': {
                  marginTop: '.5rem',
                  boxShadow: '0px 2px 6px 0px #00000040 !important',
                },
              },
            }}
            renderValue={(selected) =>
              selected
                .map((i) => filterOptions.find((o) => o.id === i)?.name)
                .join(', ')
            }
            data-testid={`insights_filter_options_${index}`}
          >
            {getOptionSelectItems(_filter.filter_name)}
          </Select>
        </FormControl>
      )}

      {deleteFilter && (
        <Button
          color="plain"
          size="lg"
          icon={['far', 'trash-can']}
          style={{ margin: '0 .25rem', padding: '0px !important' }}
          onClick={() => {
            deleteFilter(index);
          }}
          data-testid="insights_delete_filter_button"
        />
      )}
    </HStack>
  );
};

export default FilterableHighlightGrid;
